import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import MyWork from './components/MyWork';
import { ServicesSection } from './components/services/services-section';
import About from './components/About';
import More from './components/More';
import ThemeSelector from './components/ThemeSelector';
import CustomScrollbar from './components/CustomScrollbar';
import Blog from './components/Blog';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ShaderAnimation } from './components/shader-lines';

const WorkAdmin = lazy(() => import('./components/WorkAdmin'));
const BlogList = lazy(() => import('./components/BlogList'));
const BlogPost = lazy(() => import('./components/BlogPost'));

function MainLayout() {
  const { isCollapsed } = useNavigation();
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-secondary relative">
      {/* Shader Background - Only in glass mode */}
      {theme === 'glass' && (
        <div className="fixed inset-0 z-0">
          <ShaderAnimation />
        </div>
      )}

      {/* Side Panels Navigation */}
      <Navigation />

      {/* Theme Selector */}
      <ThemeSelector />

      {/* Custom Scrollbar */}
      <CustomScrollbar lineCount={50} activeLinesCount={3} />

      {/* Main Content Area - Padding container */}
      <main
        className={`relative z-10 min-h-screen py-0 md:py-6 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'ml-0 md:ml-6 mr-0 md:mr-24' : 'ml-0 md:ml-56 mr-0 md:mr-24'
        }`}
      >
        {/* Content Card - Changes based on theme */}
        <div
          className={`min-h-screen rounded-none md:rounded-2xl overflow-hidden shadow-sm transition-all duration-300 ${
            theme === 'glass'
              ? 'glass-panel'
              : 'bg-background'
          }`}
        >
          <section id="home">
            <Hero />
          </section>

          <section id="work">
            <MyWork />
          </section>

          <section id="services">
            <ServicesSection />
          </section>

          <section id="about">
            <About />
          </section>

          <section id="blog">
            <Blog />
          </section>

          <section id="more">
            <More />
          </section>

          {/* Footer */}
          <footer id="footer" className="px-6 py-12 border-t border-border">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div>
                  <p className="text-muted-foreground">
                    © 2025 Developer Portfolio. All rights reserved.
                  </p>
                </div>
                <div className="flex gap-6">
                  <a href="https://github.com/m-kaos" className="text-muted-foreground hover:text-accent transition-colors">
                    GitHub
                  </a>
                  <a href="https://x.com/war_navarro" className="text-muted-foreground hover:text-accent transition-colors">
                    Twitter
                  </a>
                  <a href="https://www.linkedin.com/in/mauricio-guerra-955628273/" className="text-muted-foreground hover:text-accent transition-colors">
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}

function BlogListLayout() {
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-secondary relative">
      {theme === 'glass' && (
        <div className="fixed inset-0 z-0">
          <ShaderAnimation />
        </div>
      )}
      <Navigation />
      <ThemeSelector />
      <CustomScrollbar lineCount={50} activeLinesCount={3} />
      <BlogList />
    </div>
  );
}

function BlogPostLayout() {
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-secondary relative">
      {theme === 'glass' && (
        <div className="fixed inset-0 z-0">
          <ShaderAnimation />
        </div>
      )}
      <Navigation />
      <ThemeSelector />
      <CustomScrollbar lineCount={50} activeLinesCount={3} />
      <BlogPost />
    </div>
  );
}

function AppContent() {
  return (
    <Router>
      <Suspense fallback={<div className="min-h-screen bg-secondary" />}>
        <Routes>
          <Route path="/" element={<MainLayout />} />
          <Route path="/blog" element={<BlogListLayout />} />
          <Route path="/blog/:slug" element={<BlogPostLayout />} />
          <Route path="/admin/work" element={<WorkAdmin />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <NavigationProvider>
          <AppContent />
        </NavigationProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
